import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Bulletproof body read: clone first so any third-party fetch
// interceptor (PostHog session recording, browser extensions, broken
// service worker) that locks the original ReadableStream can't break
// us. Then read text and JSON.parse with try/catch. Same pattern used
// by the import handlers in pages/planning/*.
const readBodySafe = async (r) => {
  let raw = '';
  try { raw = await r.clone().text(); }
  catch { try { raw = await r.text(); } catch { raw = ''; } }
  let json = null;
  try { json = raw ? JSON.parse(raw) : null; } catch { /* non-JSON body */ }
  return { raw, json };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [entitlements, setEntitlements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(
    localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token')
  );

  const getStoredRememberMe = () => localStorage.getItem('remember_me') === 'true';

  const storeToken = (newToken, rememberMe) => {
    if (rememberMe) {
      localStorage.setItem('auth_token', newToken);
      localStorage.setItem('remember_me', 'true');
      sessionStorage.removeItem('auth_token');
    } else {
      sessionStorage.setItem('auth_token', newToken);
      localStorage.removeItem('auth_token');
      localStorage.removeItem('remember_me');
    }
  };

  const clearToken = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('remember_me');
    localStorage.removeItem('saved_email');
    sessionStorage.removeItem('auth_token');
  };

  const checkAuth = useCallback(async () => {
    try {
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      let response;
      try {
        response = await fetch(`${API}/auth/me`, { headers });
      } catch {
        setUser(null);
        setToken(null);
        clearToken();
        return;
      }
      
      if (response.ok) {
        const { json: userData } = await readBodySafe(response);
        if (userData) {
          setUser(userData);
          // Refresh entitlements alongside user state.
          try {
            const er = await fetch(`${API}/signup/entitlements/me`, {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (er.ok) setEntitlements(await er.json());
          } catch { /* offline — keep last */ }
        }
        else { setUser(null); setToken(null); clearToken(); }
      } else {
        setUser(null);
        setToken(null);
        clearToken();
      }
    } catch (error) {
      console.error('Auth check failed:', error.message);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    // The public demo tour is deliberately offline — no session probe.
    if (window.location.pathname.startsWith('/demo')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password, rememberMe = false) => {
    // First-request retry: Citrix-managed Chrome sessions regularly drop
    // the very first POST while warming up the proxy connection. We try
    // twice with a short pause so the user sees a one-second delay
    // instead of a "Couldn't reach the server" toast on every login.
    const doLogin = async () => {
      let response;
      try {
        response = await fetch(`${API}/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password, remember_me: rememberMe })
        });
      } catch (e) {
        const err = new Error('Network error — could not reach server');
        err.networkLike = true;
        err.cause = e;
        throw err;
      }
      const { raw, json } = await readBodySafe(response);
      if (!response.ok) {
        const msg = (json && (json.detail || json.message))
          || raw.slice(0, 200)
          || `Login failed (HTTP ${response.status})`;
        throw new Error(msg);
      }
      if (!json || !json.access_token) {
        throw new Error('Login response was empty — please retry once.');
      }
      return json;
    };
    let json;
    try {
      json = await doLogin();
    } catch (e) {
      if (!e.networkLike) throw e;
      await new Promise(r => setTimeout(r, 800));
      json = await doLogin();
    }
    setToken(json.access_token);
    setUser(json.user);
    setEntitlements(json.entitlements || null);
    storeToken(json.access_token, rememberMe);
    if (rememberMe) {
      localStorage.setItem('saved_email', email);
    } else {
      localStorage.removeItem('saved_email');
    }
    return json;
  };

  const guestLogin = async () => {
    let response;
    try {
      response = await fetch(`${API}/auth/guest-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
    } catch {
      throw new Error('Network error — could not reach server');
    }

    const { raw, json } = await readBodySafe(response);
    if (!response.ok) {
      throw new Error((json && json.detail) || raw.slice(0, 200) || `Guest login failed (HTTP ${response.status})`);
    }
    if (!json || !json.access_token) {
      throw new Error('Guest login response was empty — please retry');
    }
    setToken(json.access_token);
    setUser({ ...json.user, is_guest: true });
    sessionStorage.setItem('auth_token', json.access_token);
    return json;
  };

  const register = async (name, email, password, company, industry) => {
    let response;
    try {
      response = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, password, company, industry })
      });
    } catch {
      throw new Error('Network error — could not reach server');
    }

    const { raw: regRaw, json: regJson } = await readBodySafe(response);
    if (!response.ok) {
      throw new Error((regJson && regJson.detail) || regRaw.slice(0, 200) || `Registration failed (HTTP ${response.status})`);
    }
    if (!regJson || !regJson.access_token) {
      throw new Error('Registration response was empty — please retry');
    }
    setToken(regJson.access_token);
    setUser(regJson.user);
    setEntitlements(regJson.entitlements || null);
    storeToken(regJson.access_token, true);
    return regJson;
  };

  const signupCompany = async ({ company_name, full_name, email, password, industry }) => {
    let response;
    try {
      response = await fetch(`${API}/signup/company`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ company_name, full_name, email, password, industry }),
      });
    } catch {
      throw new Error('Network error — could not reach server');
    }
    const { raw, json } = await readBodySafe(response);
    if (!response.ok) {
      throw new Error((json && json.detail) || raw.slice(0, 200) || `Signup failed (HTTP ${response.status})`);
    }
    setToken(json.access_token);
    setUser(json.user);
    setEntitlements(json.entitlements || null);
    storeToken(json.access_token, true);
    return json;
  };

  const processGoogleSession = async (sessionId) => {
    let response;
    try {
      response = await fetch(`${API}/auth/google-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId })
      });
    } catch {
      throw new Error('Network error — could not reach server');
    }

    const { raw: gRaw, json: gJson } = await readBodySafe(response);
    if (!response.ok) {
      throw new Error((gJson && gJson.detail) || gRaw.slice(0, 200) || `Google auth failed (HTTP ${response.status})`);
    }
    setUser(gJson || null);
    return gJson;
  };

  const logout = async () => {
    try {
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        headers
      }).catch(() => {});
    } catch {
      // Suppress logout errors
    }
    
    setUser(null);
    setToken(null);
    clearToken();
  };

  const value = {
    user,
    entitlements,
    token,
    loading,
    login,
    guestLogin,
    register,
    signupCompany,
    logout,
    processGoogleSession,
    checkAuth,
    reloadUser: checkAuth,
    refreshEntitlements: async () => {
      if (!token) return;
      try {
        const r = await fetch(`${API}/signup/entitlements/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok) setEntitlements(await r.json());
      } catch { /* offline — keep last snapshot */ }
    },
    isAdmin: user?.role === 'admin' || user?.role === 'master_admin' || user?.is_master_admin,
    isMasterAdmin: user?.is_master_admin,
    isCompanyAdmin: user?.is_company_admin || user?.is_master_admin,
    isBillingOwner: user?.is_billing_owner,
    isLegacyTenant: entitlements?.is_legacy === true,
    isTrial: entitlements?.status === 'trial',
    isTrialExpired: entitlements?.status === 'expired',
    isGuest: user?.is_guest || user?.role === 'guest',
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
